import { memo, useEffect, useRef, useState, type FC, type KeyboardEvent } from "react";
import type { PromptToken } from "../../SegmentPrompt.Mocks";
import { useSegmentFormatters, useSegmentLabel } from "../../UseSegmentLabel";

const GHOST_LINE_END = "​";

interface PromptComposerProps {
  text: string;
  tokens: PromptToken[];
  suggestion: string;
  canFill: boolean;
  onTextChange: (_text: string) => void;
  onFill: () => void;
  onAcceptSuggestion: () => void;
}

const PromptComposer: FC<PromptComposerProps> = ({
  text,
  tokens,
  suggestion,
  canFill,
  onTextChange,
  onFill,
  onAcceptSuggestion,
}) => {
  const label = useSegmentLabel();
  const { formatValue } = useSegmentFormatters();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const showTokens = !isEditing && Boolean(text.trim());

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing) {
      return;
    }
    const isCaretAtEnd = event.currentTarget.selectionStart === text.length;
    if (event.key === "Tab" && !event.shiftKey && suggestion && isCaretAtEnd) {
      event.preventDefault();
      onAcceptSuggestion();
    } else if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onFill();
    }
  };

  return (
    <div className="segment-prompt__box">
      {showTokens ? (
        <button
          type="button"
          className="segment-prompt__field segment-prompt__tokens"
          aria-label={label("prompt.editAria")}
          onClick={() => setIsEditing(true)}
        >
          {tokens.map((token, index) =>
            token.kind === "text" ? (
              <span key={index} className="segment-prompt__text">
                {token.text}
              </span>
            ) : (
              <span key={index} className={`segment-prompt__token segment-prompt__token--${token.kind}`}>
                {token.text}
                {token.matchNumber !== undefined && (
                  <span className="segment-prompt__token-badge">{formatValue(token.matchNumber)}</span>
                )}
              </span>
            )
          )}
        </button>
      ) : (
        <div className="segment-prompt__field segment-prompt__editor">
          <div className="segment-prompt__ghost" aria-hidden>
            <span className="segment-prompt__ghost-typed">{text}</span>
            {isEditing && suggestion}
            {GHOST_LINE_END}
          </div>
          <textarea
            ref={inputRef}
            rows={1}
            className="segment-prompt__input"
            aria-label={label("prompt.title")}
            placeholder={label("prompt.placeholder")}
            value={text}
            onChange={event => onTextChange(event.target.value)}
            onFocus={() => setIsEditing(true)}
            onBlur={() => setIsEditing(false)}
            onKeyDown={handleKeyDown}
          />
        </div>
      )}
      <button
        type="button"
        className="button button--primary segment-prompt__fill"
        disabled={!canFill}
        onClick={onFill}
      >
        {label("prompt.fill")}
      </button>
    </div>
  );
};

export default memo(PromptComposer);
